/**
 * UL-7905 / UL-7906 / UL-7908 -- DSP insertion order targeting.
 *
 * The catalogue is keyed on `(customer, insertion_order_id)`, not on the id alone: an insertion
 * order id is only unique within a DSP. Anything that maps or looks one up has to use both.
 */

/** A row of the standing catalogue, refreshed from the DSP logs. Never typed by hand. */
export type InsertionOrder = {
    id: number;
    customer: string;
    advertiser: string | null;
    insertion_order_id: string;
    insertion_order_name: string | null;

    /** First and last playout *in the data* -- not a campaign's dates. */
    min_date: string | null;
    max_date: string | null;

    created_at: string | null;
    updated_at: string | null;
};

/** What a condition can match on. Mirrors the API's CampaignInsertionOrderParameter enum. */
export enum InsertionOrderAttribute {
    insertionOrderId = 'insertion_order_id',
    insertionOrderName = 'insertion_order_name',
    advertiser = 'advertiser',
}

export enum InsertionOrderSearchType {
    exact = 'exact',
    contains = 'contains',
    startsWith = 'starts_with',
    endsWith = 'ends_with',
}

/**
 * One AND'ed part of a rule. A rule may use each attribute at most once, and `insertion_order_id`
 * may only be matched exactly -- partial-matching a surrogate id attached unrelated records in
 * production once already.
 */
export type InsertionOrderCondition = {
    id?: number;
    parameter: InsertionOrderAttribute;
    search_type: InsertionOrderSearchType;
    identifier: string;
};

/** A targeting rule: conditions AND'ed together, rules OR'd with each other. */
export type InsertionOrderRule = {
    id: number;
    campaign_id: number;

    /** null means any DSP. */
    customer: string | null;

    /**
     * An OVERLAP bound against the catalogue's min_date/max_date, not the campaign's dates: an
     * insertion order qualifies if it played at any point inside the range.
     */
    date_from: string | null;
    date_to: string | null;

    /** How many insertion orders this rule has actually brought in. */
    insertion_order_count: number;

    conditions: InsertionOrderCondition[];
    created_at: string | null;
    updated_at: string | null;
};

/** An insertion order as it appears on a campaign: the catalogue row, plus how it got here. */
export type CampaignInsertionOrder = {
    id: number;
    campaign_id: number;
    customer: string;
    insertion_order_id: string;

    insertion_order_name: string | null;
    advertiser: string | null;
    min_date: string | null;
    max_date: string | null;

    /**
     * Every rule that attached it. The API groups one row per insertion order, so an insertion order
     * matched by two rules appears once; deleting one rule leaves it attached while another remains.
     */
    parameter_ids: number[];

    /** The campaign Default until someone applies another; null only if the campaign has no dates. */
    schedule_id: number | null;

    cpm: string | null;

    /**
     * UL-8133 -- the impressions this insertion order is planned to deliver, typed on the schedule
     * page (up to 2dp). A JSON number, unlike `cpm`, which arrives as a decimal string. The
     * campaign's `expected_impacts` is the sum of these on a `vistar_us2` campaign.
     */
    impressions_target: number | null;

    created_at: string | null;
    updated_at: string | null;
};

/**
 * What the detail endpoint writes and answers. It writes only the keys sent, so a CPM save never
 * touches the target and the other way round.
 */
export type InsertionOrderDetailPayload = InsertionOrderKey & {
    cpm?: number | null;
    impressions_target?: number | null;
};

export type InsertionOrderDetailResponse = {
    success: boolean;
    data: InsertionOrderKey & {
        campaign_id?: number;
        cpm: string | null;
        impressions_target?: number | null;
        /** The campaign's new Expected Impressions: the sum of its targets. */
        expected_impacts?: number | null;
    };
};

/**
 * UL-8136 -- a planned line: an insertion order's target and schedule, entered before the real
 * insertion order has delivered. Its target counts in the campaign's Expected Impressions straight
 * away. Linking it to a catalogue insertion order moves the target and schedule onto that IO and
 * the placeholder disappears.
 */
export type CampaignInsertionOrderPlaceholder = {
    id: number;
    label: string;
    impressions_target: number | null;
    /** The Default when created; schedules apply as an IO's, through the reserved key. */
    schedule_id: number | null;
    created_at: string | null;
};

export type InsertionOrderPlaceholderPayload = {
    label?: string;
    impressions_target?: number | null;
};

/**
 * The schedule key the API reserves for a placeholder: `('__placeholder__', 'placeholder-{id}')`.
 * Schedule apply / remove take it exactly like an insertion order's key.
 */
export const PLACEHOLDER_CUSTOMER = '__placeholder__';

export const placeholderScheduleKey = (placeholderId: number): InsertionOrderKey => ({
    customer: PLACEHOLDER_CUSTOMER,
    insertion_order_id: `placeholder-${placeholderId}`,
});

/** One venue on the campaign's allowed list. Anything not on it is UNBOOKED. */
export type CampaignAllowedVenue = {
    id: number;
    public_id: string;
    network_id: string | null;
    partner_venue_id: string | null;
    venue_name: string | null;
    venue_type: string | null;
    targeting_id: string | null;
};

/**
 * The result of an allowed venue upload.
 *
 * The counts are the point, not the success flag: no venue list means nothing is ever unbooked, so
 * the danger is not the missing file but the incomplete one. Rows read versus inserted is what
 * catches a plan that lost a network on the way.
 */
export type AllowedVenueUploadSummary = {
    rows_read: number;
    inserted: number;
    duplicates: number;
    rejected: number;
    rejections: { line: number; reason: string }[];
};

/** Identifies an insertion order the way the catalogue does -- an id is only unique within a DSP. */
export type InsertionOrderKey = {
    customer: string;
    insertion_order_id: string;
};

/** The result of saving several rules at once. Duplicates are skipped with a reason, not failed. */
export type InsertionOrderRuleBatchResult = {
    created: InsertionOrderRule[];
    skipped: { index: number; rule_id: number | null; message: string }[];
};

/** The live list an upload would replace -- the mockup's "Replaces the list uploaded …" row. */
export type AllowedVenuePreviousList = {
    count: number;
    uploaded_at: string | null;
};

export type AllowedVenueUploadResponse = {
    success: boolean;
    dry_run: boolean;
    message?: string;
    summary?: AllowedVenueUploadSummary;
    previous: AllowedVenuePreviousList;
};
